/* eslint-disable @next/next/no-img-element */
import useTeamPlayers from '@amf/shared/data/hooks/useTeamPlayers';
import {
  SeasonFragment,
  TeamListItemFragment,
  TopPlayerFragment,
  TopPlayersFragment,
} from '@amf/shared/types/graphql';
import React, { useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import Loader from '@amf/shared/components/utils/Loader';
import { getInitials } from '@amf/shared/utils/string';
import Link from 'next/link';
import { findTeamDefaults } from '@amf/shared/data/Team';

import SeasonSelect, { SeasonSelectData } from '../menu/SeasonSelect';
import Button from '../button/Button';

interface PromotedItemProps {
  players: TopPlayerFragment[];
  title: string;
  value: string;
  prefixes: { player: string };
}

function PromotedItem({ players, title, prefixes, value }: PromotedItemProps) {
  return (
    <div className='TeamPlayerStats__promotedItem'>
      {players[0] && (
        <Link href={`${prefixes.player}/${players[0].id}`}>
          <a className='TeamPlayerStats__player'>
            {players[0].photoUrl ? (
              <img
                className='TeamPlayerStats__playerAvatar'
                src={players[0].photoUrl}
                alt={players[0].fullName}
              />
            ) : (
              <div className='TeamPlayerStats__playerAvatar'>
                {getInitials(players[0].givenName, players[0].familyName)}
              </div>
            )}
            <div className='TeamPlayerStats__playerName'>{players[0].fullName}</div>
            <div className='TeamPlayerStats__playerSubtitle'>{title}</div>
            <div className='TeamPlayerStats__playerCount'>
              {players[0].value} {value}
            </div>
          </a>
        </Link>
      )}
      <table className='TeamPlayerStats__promotedTable'>
        <tbody>
          {players.slice(1).map(({ id, fullName, value }) => (
            <tr key={id}>
              <th>
                <Link href={`${prefixes.player}/${id}`}>
                  <a>{fullName}</a>
                </Link>
              </th>
              <td>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface TableProps {
  players: TopPlayersFragment;
  prefixes: { player: string };
  disableAssists?: boolean;
  disablePoints?: boolean;
}

function Table({ players, prefixes, disableAssists, disablePoints }: TableProps) {
  const { t } = useTranslate();
  return (
    <div className='TeamPlayerStats__table'>
      <div className='TeamPlayerStats__column'>
        {players.mostGoalsInOneMatch[0] && (
          <div className='TeamPlayerStats__row'>
            <div className='TeamPlayerStats__cell'>{t('team.playerStats.mostGoalsInOneMatch')}</div>
            <Link href={`${prefixes.player}/${players.mostGoalsInOneMatch[0].id}`}>
              <a className='TeamPlayerStats__cell TeamPlayerStats__cell--player'>
                {players.mostGoalsInOneMatch[0] && players.mostGoalsInOneMatch[0].fullName} (
                {players.mostGoalsInOneMatch[0].value})
              </a>
            </Link>
          </div>
        )}
        {players.mostAssistsInOneMatch[0] && !disableAssists && (
          <div className='TeamPlayerStats__row'>
            <div className='TeamPlayerStats__cell'>
              {t('team.playerStats.mostAssistsInOneMatch')}
            </div>
            <Link href={`${prefixes.player}/${players.mostAssistsInOneMatch[0].id}`}>
              <a className='TeamPlayerStats__cell TeamPlayerStats__cell--player'>
                {players.mostAssistsInOneMatch[0] && players.mostAssistsInOneMatch[0].fullName} (
                {players.mostAssistsInOneMatch[0].value})
              </a>
            </Link>
          </div>
        )}
        {players.mostPointsInOneMatch[0] && !disablePoints && (
          <div className='TeamPlayerStats__row'>
            <div className='TeamPlayerStats__cell'>
              {t('team.playerStats.mostPointsInOneMatch')}
            </div>
            <Link href={`${prefixes.player}/${players.mostPointsInOneMatch[0].id}`}>
              <a className='TeamPlayerStats__cell TeamPlayerStats__cell--player'>
                {players.mostPointsInOneMatch[0] && players.mostPointsInOneMatch[0].fullName} (
                {players.mostPointsInOneMatch[0].value})
              </a>
            </Link>
          </div>
        )}
      </div>
      <div className='TeamPlayerStats__column'>
        {players.mostRedCards[0] && (
          <div className='TeamPlayerStats__row'>
            <div className='TeamPlayerStats__cell'>{t('team.playerStats.mostRedCards')}</div>
            <Link href={`${prefixes.player}/${players.mostRedCards[0].id}`}>
              <a className='TeamPlayerStats__cell TeamPlayerStats__cell--player'>
                {players.mostRedCards[0] && players.mostRedCards[0].fullName} (
                {players.mostRedCards[0].value})
              </a>
            </Link>
          </div>
        )}
        {players.mostYellowCards[0] && (
          <div className='TeamPlayerStats__row'>
            <div className='TeamPlayerStats__cell'>{t('team.playerStats.mostYellowCards')}</div>
            <Link href={`${prefixes.player}/${players.mostYellowCards[0].id}`}>
              <a className='TeamPlayerStats__cell TeamPlayerStats__cell--player'>
                {players.mostYellowCards[0] && players.mostYellowCards[0].fullName} (
                {players.mostYellowCards[0].value})
              </a>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

interface TeamPlayerStatsProps {
  team: TeamListItemFragment;
  seasons: SeasonFragment[];
  prefixes: { player: string; stats?: string };
  title?: string;
  disableAssists?: boolean;
  disablePoints?: boolean;
}

function TeamPlayerStats({
  team,
  seasons,
  title,
  prefixes,
  disableAssists,
  disablePoints,
}: TeamPlayerStatsProps) {
  const { t } = useTranslate();

  const defaultValues: SeasonSelectData = useMemo(() => {
    const { league, season } = findTeamDefaults(team, seasons);
    return {
      league: league?.id ?? seasons[0]?.leagues[0]?.id,
      season: season?.id ?? seasons[0]?.id,
    };
  }, [seasons, team]);

  const [filter, setFilter] = useState<SeasonSelectData>(defaultValues);
  const { players, loading } = useTeamPlayers(filter.league, filter.season, team.id);

  return (
    <div className='TeamPlayerStats'>
      <div className='TeamPlayerStats__header'>
        {title && <h2 className='TeamPlayerStats__title'>{title}</h2>}
        <SeasonSelect
          team={team}
          values={defaultValues}
          seasons={seasons}
          onUpdate={async data => setFilter(data)}
          allSeasonsAllowed
          allLeaguesAllowed
        />
      </div>
      {loading && <Loader size='small'>{t('loading')}</Loader>}
      {!loading && players && (
        <div className='TeamPlayerStats__content'>
          <div className='TeamPlayerStats__promotedList'>
            <PromotedItem
              players={players.mostGoals}
              title={t('team.playerStats.mostGoals')}
              value={t('team.playerStats.mostGoals.value')}
              prefixes={prefixes}
            />
            {!disableAssists && (
              <PromotedItem
                players={players.mostAssists}
                title={t('team.playerStats.mostAssists')}
                value={t('team.playerStats.mostAssists.value')}
                prefixes={prefixes}
              />
            )}
            {!disablePoints && (
              <PromotedItem
                players={players.mostPoints}
                title={t('team.playerStats.mostPoints')}
                value={t('team.playerStats.mostPoints.value')}
                prefixes={prefixes}
              />
            )}
            <PromotedItem
              players={players.mostMatches}
              title={t('team.playerStats.mostMatches')}
              value={t('team.playerStats.mostMatches.value')}
              prefixes={prefixes}
            />
          </div>
          <Table
            players={players}
            prefixes={prefixes}
            disableAssists={disableAssists}
            disablePoints={disablePoints}
          />
          {prefixes.stats && (
            <Link href={`${prefixes.stats}/${team.id}`}>
              <a style={{ margin: 'auto', width: 'fit-content', display: 'block' }}>
                <Button variant='dark-outline'>{t('team.playerStats.more')}</Button>
              </a>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export default TeamPlayerStats;
